/**
 * Nightlife N7: what a club night's row and sheet say, decided in a file with
 * no imports — the contract `rowMeta.ts`, `entities.ts` and `affinity.ts`
 * keep, so the Worker's offline suite loads it directly and `npm test` gates
 * the rules (`test/night-card.test.ts`).
 *
 * **Nothing here writes a sentence** (B1, H2). Every string returned is a
 * name the page printed, a clock the page printed, or one of the fixed words
 * in `NIGHT_WORDS` — the same rule `rowMeta.ts`'s `FIXED_LABELS` states for
 * the rest of the row.
 *
 * The shapes are `src/harvest/normalise/night.ts`'s, copied rather than
 * imported (the app does not import the Worker's source; `types.ts` copies
 * `ClassicalFacts` the same way). A rename there is a compile error in the
 * Worker's suite and a silent `undefined` here, which is why every reader
 * below takes each field as optional.
 */

/** `entries.night.format` (the N1 schema's column comment, `NIGHT_FORMATS`). */
export type NightFormat = 'open_air_day' | 'indoor_night' | 'bar' | 'listening'

/** One printed name. `name` is as printed minus the markers read off it;
 *  `src` is the whole printed item where it differs. */
export interface LineupSlot {
  name: string
  role: 'dj' | 'live'
  slot?: string
  /** One half (or third) of an "A b2b B" pairing. The normaliser marks every
   *  member and nothing else, so a pairing is a run of consecutive `b2b`
   *  slots — see `lineupUnits` for what that cannot tell apart. */
  b2b?: true
  floor?: string
  src?: string
}

/** `entries.night` — `NightFacts` in `src/harvest/normalise/night.ts`. */
export interface NightFacts {
  lineup?: LineupSlot[]
  /** HH:MM, Berlin wall clock, as printed. */
  doors?: string
  closes?: string
  format?: NightFormat
  floors?: string[]
  genre_tags?: string[]
  /** `'lineup'` when the DJ facts pass derived `genre_tags` from the
   *  lineup's Discogs styles because the venue printed none; absent when
   *  printed. */
  genre_src?: string
}

/** One `feed_lite.series` element: the thin `[{id, role}]` shape `people`
 *  has there (K11). Ordered host first by the view. */
export interface SeriesEdge {
  id: string
  role: 'host' | 'guest' | string
}

/** One `series` row, the columns the app reads. */
export interface SeriesRow {
  id: string
  name: string
  kind: string
  home_venue_id: string | null
}

/** Every word this file can add to a row that is not a printed name or a
 *  printed clock. A new one is added here on purpose, as `FIXED_LABELS` is. */
export const NIGHT_WORDS = ['doors', 'until', 'b2b', '(live)', 'more', 'open air', 'bar', 'listening'] as const

/** How many names the row's lineup line prints before "+ n more". */
export const LINEUP_SHOWN = 4

// ---------------------------------------------------------------------------
// The lineup
// ---------------------------------------------------------------------------

const norm = (s: string) => s.trim().toLowerCase()

/**
 * The lineup as the units a line may not split: a single name, or a b2b run
 * kept whole. Duplicates dropped (first kept) — a page that lists a DJ once
 * per floor or once per set means one person to the reader.
 *
 * A b2b run is consecutive slots marked `b2b` on the same floor. What the
 * normaliser's shape cannot say is where one pairing ends and the next
 * begins: "A b2b B, C b2b D" arrives as four marked slots in a row, the same
 * as "A b2b B b2b C b2b D". Read as one run, which over-joins rather than
 * splits — a pairing is never printed apart, which is the rule that matters
 * on a card. A group index on the slot (N2) would settle it.
 */
export function lineupUnits(lineup: LineupSlot[] | null | undefined): LineupSlot[][] {
  const seen = new Set<string>()
  const units: LineupSlot[][] = []
  let prev: LineupSlot | null = null
  for (const s of lineup ?? []) {
    const name = s?.name?.trim()
    if (!name) { prev = null; continue }
    const key = norm(name)
    if (seen.has(key)) { prev = null; continue }
    seen.add(key)
    const joins = !!s.b2b && !!prev?.b2b && (prev.floor ?? '') === (s.floor ?? '') && units.length > 0
    if (joins) units[units.length - 1].push(s)
    else units.push([s])
    prev = s
  }
  return units
}

/** One name as the line prints it: a live act is marked, a DJ is not. */
const slotText = (s: LineupSlot) => (s.role === 'live' ? `${s.name.trim()} (live)` : s.name.trim())

/**
 * The row's lineup line: up to `max` names joined by " · ", then "+ n more".
 *
 * A b2b pairing is one unit — "Ben UFO b2b Joy Orbison" — and is never cut
 * across the "+ n more": a pairing that would take the line past `max` waits
 * for the count instead, unless it is the first thing on the line (a
 * five-way b2b is still the night's headline). `n` counts names, not units,
 * because "+ 2 more" that turned out to be one pairing would be the line
 * undercounting who is playing.
 */
export function lineupLine(night: NightFacts | null | undefined, max = LINEUP_SHOWN): string | null {
  const units = lineupUnits(night?.lineup)
  if (units.length === 0) return null
  const shown: string[] = []
  let names = 0
  let i = 0
  for (; i < units.length; i++) {
    const u = units[i]
    if (shown.length > 0 && names + u.length > max) break
    shown.push(u.map(slotText).join(' b2b '))
    names += u.length
  }
  const rest = units.slice(i).reduce((n, u) => n + u.length, 0)
  const line = shown.join(' · ')
  return rest > 0 ? `${line} + ${rest} more` : line
}

// ---------------------------------------------------------------------------
// Doors, format
// ---------------------------------------------------------------------------

/** A printed clock the line may repeat: H:MM or HH:MM, and nothing else. A
 *  field that holds "late" or "tba" is not a time and is not printed. */
const CLOCK = /^([01]?\d|2[0-4])[:.][0-5]\d$/
const clock = (s: string | null | undefined): string | null => {
  const t = (s ?? '').trim()
  if (!CLOCK.test(t)) return null
  const [h, m] = t.split(/[:.]/)
  return `${h.padStart(2, '0')}:${m}`
}

/**
 * "doors 22:00 – 10:00", "doors 23:59", "until 06:00", or null.
 *
 * The page's own two clocks and nothing inferred: the showing's start is not
 * copied in as doors (the normaliser's own rule — a second place for one
 * fact), so a night that printed neither gets null and the row keeps the
 * showing time its kind line already states.
 */
export function doorsLine(night: NightFacts | null | undefined): string | null {
  const doors = clock(night?.doors)
  const closes = clock(night?.closes)
  if (doors && closes) return `doors ${doors} – ${closes}`
  if (doors) return `doors ${doors}`
  if (closes) return `until ${closes}`
  return null
}

/** `format` as the one word a row prints. `indoor_night` is the default a
 *  club is, and saying so says nothing — null, like an unset format. */
export const FORMAT_WORD: Record<NightFormat, string | null> = {
  open_air_day: 'open air',
  indoor_night: null,
  bar: 'bar',
  listening: 'listening',
}

export function formatWord(night: NightFacts | null | undefined): string | null {
  const f = night?.format
  return f && Object.prototype.hasOwnProperty.call(FORMAT_WORD, f) ? FORMAT_WORD[f] : null
}

/** The row's what line for a club night: the format word, then the doors.
 *  Null where the night printed neither — the line is then absent, which is
 *  what `whatLine` has always returned for a club. */
export function clubWhat(night: NightFacts | null | undefined): string | null {
  const parts = [genreWord(night), formatWord(night), doorsLine(night)].filter((p): p is string => !!p)
  return parts.length ? parts.join(' · ') : null
}

/** The first two genre tags, lowercased and joined — the one line of
 *  context most club pages actually carry (AEDEN, Crack Bellmer, Cassiopeia
 *  print a genre on every row; a lineup of unknown names says nothing about
 *  the sound, "house, disco" does). The venue's own where it printed one;
 *  otherwise, since the DJ facts pass (`genre_src: 'lineup'`), the two
 *  styles most of the lineup is filed under on Discogs — drawn the same,
 *  because on a card both answer "what does this night sound like". Two,
 *  because the what line is one line: a third tag is the page's own noise. */
export function genreWord(night: NightFacts | null | undefined): string | null {
  const tags = (night?.genre_tags ?? []).map(t => (t ?? '').trim()).filter(Boolean)
  if (!tags.length) return null
  return tags.slice(0, 2).map(t => t.toLowerCase()).join(', ')
}

// ---------------------------------------------------------------------------
// Series
// ---------------------------------------------------------------------------

/** The id of the series whose night this is, or null. The view orders host
 *  first, but this reads the role rather than trusting the position: a row
 *  whose only edge is a guest has no host, and printing the guest as the
 *  night's name would be the card claiming whose night it is. */
export function hostSeries(series: SeriesEdge[] | null | undefined): string | null {
  for (const s of series ?? []) if (s?.id && s.role === 'host') return s.id
  return null
}

/** A series' name by id, or null — while the `series` fetch is in flight, if
 *  it failed, or for an id it does not hold. Never the slug: "koffaein" on a
 *  card is the database talking. The series page, which has nothing else to
 *  put in its header, falls back to the id itself once the fetch has
 *  answered, as Browse's `?person=` header does. */
export function seriesName(byId: ReadonlyMap<string, { name: string }> | null | undefined, id: string | null | undefined): string | null {
  if (!id || !byId) return null
  const name = byId.get(id)?.name?.trim()
  return name || null
}

/** The row's eyebrow: the host party's name, or null (nothing drawn). */
export function clubEyebrow(
  e: { type: string; series?: SeriesEdge[] | null },
  byId: ReadonlyMap<string, { name: string }> | null | undefined,
): string | null {
  if (e.type !== 'club_night') return null
  return seriesName(byId, hostSeries(e.series))
}

/** Everything the row draws for a club night, beyond what `rowMeta.ts`
 *  already says (kind, when, where). Null fields leave the row's own. */
export function clubRow(
  e: { type: string; night?: NightFacts | null; series?: SeriesEdge[] | null },
  byId: ReadonlyMap<string, { name: string }> | null | undefined,
): { eyebrow: string | null; what: string | null; lineup: string | null } | null {
  if (e.type !== 'club_night') return null
  const night = e.night ?? null
  return { eyebrow: clubEyebrow(e, byId), what: clubWhat(night), lineup: lineupLine(night) }
}

/**
 * The series page's list: every row carrying an edge to `seriesId`, the
 * nights it hosts first and the ones it guests on after, each soonest first.
 * `sortKey` is the row's own date to sort by (the caller's `next_showing ??
 * run_start`); a row with none sorts last in its half.
 */
export function seriesNights<T extends { id: string; series?: SeriesEdge[] | null }>(
  rows: readonly T[],
  seriesId: string,
  sortKey: (r: T) => string | null | undefined,
): T[] {
  const role = (r: T) => (r.series ?? []).find(s => s?.id === seriesId)?.role ?? null
  return rows
    .filter(r => role(r) != null)
    .map(r => ({ r, host: role(r) === 'host' ? 0 : 1, at: sortKey(r) ?? '￿' }))
    .sort((a, b) => a.host - b.host || (a.at < b.at ? -1 : a.at > b.at ? 1 : a.r.id < b.r.id ? -1 : 1))
    .map(x => x.r)
}
